import React from 'react';
import { Table } from 'antd';
import type { TableProps } from 'antd';
import EmptyState from './EmptyState';

interface DataTableProps<T> extends TableProps<T> {
    /** Message when there are no rows. */
    emptyText?: React.ReactNode;
    /**
     * Double-clicking a row runs this — pass the row's primary destination
     * (manage → else view → else edit). Rows get a pointer cursor when set.
     */
    onRowOpen?: (record: T) => void;
}

/** Common width for the single "⋯" actions column, applied to every table. */
const ACTIONS_COLUMN_WIDTH = 90;

const isActionsColumn = (col: any) => {
    const key = col?.key ?? col?.dataIndex;
    const title = typeof col?.title === 'string' ? col.title : '';
    return key === 'actions' || key === 'action' || title === 'Actions' || title === 'Action';
};

/**
 * Standard table for all list pages: size="middle", rowKey="id",
 * pageSize 10 with "x-y of z items" total, unified empty state.
 * Defaults to `scroll={{ x: 'max-content' }}` so wide tables scroll
 * horizontally on their own instead of overflowing the page.
 * The Actions column is normalized to one common narrow, centered width
 * everywhere (it only holds a single "⋯" menu button).
 * Pass `onRowOpen` to make double-clicking a row open its primary page.
 * Pass pagination={false} or any Table prop to override deliberately.
 */
function DataTable<T extends object = any>({ emptyText, pagination, locale, scroll, columns, onRowOpen, onRow, ...rest }: DataTableProps<T>) {
    const normalizedColumns = React.useMemo(
        () =>
            columns?.map(col =>
                isActionsColumn(col)
                    ? { align: 'center' as const, ...col, width: ACTIONS_COLUMN_WIDTH }
                    : col,
            ),
        [columns],
    );

    const mergedOnRow: DataTableProps<T>['onRow'] = onRowOpen
        ? (record, index) => {
              const base = onRow?.(record, index) ?? {};
              return {
                  ...base,
                  style: { cursor: 'pointer', ...(base.style || {}) },
                  onDoubleClick: e => {
                      base.onDoubleClick?.(e);
                      onRowOpen(record);
                  },
              };
          }
        : onRow;

    return (
        <Table<T>
            size="middle"
            rowKey="id"
            scroll={{ x: 'max-content', ...scroll }}
            columns={normalizedColumns}
            onRow={mergedOnRow}
            pagination={
                pagination === false
                    ? false
                    : {
                          pageSize: 10,
                          showSizeChanger: false,
                          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                          ...pagination,
                      }
            }
            locale={{ emptyText: <EmptyState description={emptyText ?? 'No data found'} />, ...locale }}
            {...rest}
        />
    );
}

export default DataTable;
